import { Box, Button, Typography } from '@mui/material';
import { DataTable, Column } from 'src/components/UI/DataTable';
import {
  cardContainerWithCommonSx,
  contentWrapperSx,
  headerContainerSx,
  titleSx,
  headerActionsSx,
  selectedCountSx,
  totalPriceSx,
  removeAllButtonSx,
  descriptionSx
} from '../styles/createCampaign';
import { AvailableNewCitation } from '../types';

export type ExistingCitationsTableRowData = {
  id: number;
  citationSite: string;
  businessName: string;
  address: string;
  zip: string;
  phoneNumber: string;
  notes: string[];
  cost: number;
};

export type AvailableNewCitationsRowData = AvailableNewCitation;

export interface SelectCitationsProps<T extends { id: number }> {
  title: string;
  description: string;
  data: T[];
  columns: Column<T>[];
  selected: (number | string)[];
  totalPrice: string | number;
  onSelect: (id: number | string) => void;
  onSelectMultiple: (ids: (number | string)[], select: boolean) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAll: () => void;
  orderBy: string;
  order: 'asc' | 'desc';
  onSort: (property: string) => void;
  getRowId?: (row: T) => number | string;
  actionButton: () => JSX.Element;
  noDataFallbackUI: () => JSX.Element;
}

export const SelectCitations = <T extends { id: number }>({
  title,
  description,
  data,
  columns,
  selected,
  totalPrice,
  onSelect,
  onSelectMultiple,
  onSelectAll,
  onRemoveAll,
  orderBy,
  order,
  onSort,
  getRowId,
  actionButton = () => null,
  noDataFallbackUI = () => null
}: SelectCitationsProps<T>) => {
  const formatPrice = (price: string | number): string => {
    if (typeof price === 'number') {
      return `$${price.toFixed(2)}`;
    }
    return price;
  };

  return (
    <Box sx={cardContainerWithCommonSx}>
      <Box sx={contentWrapperSx}>
        <Box sx={headerContainerSx}>
          <Typography sx={titleSx}>{title}</Typography>
          {data.length > 0 && (
            <Box sx={headerActionsSx}>
              <Typography sx={selectedCountSx}>
                {selected.length}/{data.length} Selected
              </Typography>
              <Typography sx={totalPriceSx}>
                Total: {formatPrice(totalPrice)}
              </Typography>
              {actionButton()}
            </Box>
          )}
        </Box>
        {data.length > 0 && (
          <Box>
            <Typography sx={descriptionSx}>{description}</Typography>
          </Box>
        )}
        {data.length === 0 && noDataFallbackUI()}
      </Box>

      {data.length > 0 && (
        <DataTable<T>
          data={data}
          columns={columns}
          getRowId={getRowId || ((row) => row.id)}
          selected={selected}
          onSelect={onSelect}
          onSelectMultiple={onSelectMultiple}
          onSelectAll={onSelectAll}
          orderBy={orderBy}
          order={order}
          onSort={onSort}
          pagination={true}
        />
      )}
    </Box>
  );
};
